import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, Observable, of } from 'rxjs';
import { DeclineQuoteResponse, DeclineQuoteResponseData, OpenQuoteRequestResponse, OpenQuoteRequestsItem, OpenQuoteRequestsResponse, QuoteRequestFileResponse } from './quote-requests.types';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';

@Injectable({ providedIn: 'root' })
export class OpenQuoteRequestsWidgetService {
  private _httpClient = inject(HttpClient);
  openQR = signal<OpenQuoteRequestsItem[]>([]);
  queuedQR = signal<OpenQuoteRequestsItem[]>([]);
  totalOpenQR = signal<number>(0);
  selectedOpenQR = signal<OpenQuoteRequestsItem>(null);
  _cathyService = inject(CathyService);
  checkedQuoteRequestIds = signal<{ [key: number]: boolean }>({});
  isLoadingOpenQuoteRequest = signal<boolean>(false);

  addCheckedQuoteRequestIds(quoteRequestId: number, checked: boolean) {
    const checkedQuoteRequestIds = this.checkedQuoteRequestIds();
    checkedQuoteRequestIds[quoteRequestId] = checked;
    this.checkedQuoteRequestIds.set(checkedQuoteRequestIds);
    // console.log('checkedQuoteRequestIds',checkedQuoteRequestIds);
  }

  getOpenQuoteRequests(filters: any = {}): Observable<OpenQuoteRequestsItem[]> {
    this.isLoadingOpenQuoteRequest.set(true);
    const params = filters || {
      'task_type_id[in]': '1,7',
    };
    return this._httpClient
      .get<OpenQuoteRequestsResponse>(
        `${environment.apiBaseUrl}/v4/quote/requests/stage/active`, {
          params: params,
        },
      ).pipe(
        map((response) => {
          this.isLoadingOpenQuoteRequest.set(false);
          this.openQR.set(response.data.quote_requests.quote_requests);
          this.totalOpenQR.set(response.data.quote_requests.total_quote_requests);
          return response.data.quote_requests.quote_requests;
        }),
      );
  }

  loadQuoteRequest(quoteRequestId: number): Observable<OpenQuoteRequestsItem> {
    return this._httpClient
      .get<OpenQuoteRequestResponse>(
        `${environment.apiBaseUrl}/v4/quote/load-request/${quoteRequestId}`
      ).pipe(
        map((response) => {
          return response.data.quote_request.quote_request;
        }),
      );
  }

  setSelectedQuoteOption(quoteRequestId: number, quoteId: number): Observable<OpenQuoteRequestsItem> {

    return this._httpClient
      .post<OpenQuoteRequestsResponse>(
        `${environment.apiBaseUrl}/v4/quote/request/${quoteRequestId}/bond/${quoteId}`, {},
      ).pipe(
        map((response) => {
          if (response && response.success) {
            this._cathyService.success(response.message);
          }
          return response.data.quote_requests.quote_requests[0];
        }),
      );
  }

  setShippingOption(quoteRequestId: number, quoteId: number, shippingOptionId: number): Observable<OpenQuoteRequestsResponse> {
    return this._httpClient
      .post<OpenQuoteRequestsResponse>(
        `${environment.apiBaseUrl}/v4/quote/request/${quoteRequestId}/shipping/${quoteId}`,
        { shipping_option_id: shippingOptionId }
      ).pipe(
        map((response) => {
          if (response && response.success) {
            this._cathyService.success(response.message);
          }
          return response;
        }),
      );
  }

  readFile(quoteRequestId: number, file_id: number) {
    return this._httpClient.get<QuoteRequestFileResponse>(
      `${environment.apiBaseUrl}/v4/quote/request/${quoteRequestId}/read-file/${file_id}`,
    ).pipe(
      map((response: QuoteRequestFileResponse) => {
        if (response?.success && response.data?.quote_request?.file?.file_content) {
          const file = response.data.quote_request.file;
          const fileContentBase64 = file.file_content;

          // Decode base64 string
          const byteCharacters = atob(fileContentBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);

          // Create blob and trigger download
          const blob = new Blob([byteArray], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        return response;
      }),
    );
  }

  declineQuote(quoteRequestId: number): Observable<DeclineQuoteResponseData> {
    const params = {
      quote_request_id: quoteRequestId,
    };
    return this._httpClient.post<DeclineQuoteResponse>(
      `${environment.apiBaseUrl}/v4/quote/request/${quoteRequestId}/decline`,
      params,
    ).pipe(
      map((response) => {
        // console.log('response', response);
        return response.data;
      }),
    );
  }

  clearData() {
    this.openQR.set([]);
    this.queuedQR.set([]);
    this.totalOpenQR.set(0);
    this.selectedOpenQR.set(null);
    this.checkedQuoteRequestIds.set({});
  }
}
